import type {
  GenericError,
  WsCommand,
  RequestStatus,
  JsonWsSuccessResponse,
} from '@skynet/data-ws-core';

export enum AccountCommands {
  Accounts = 'Accounts',
  EnableAccount = 'EnableAccount',
  DisableAccount = 'DisableAccount',
}

interface JsonLinechartPart {
  date: string;
  value: number;
}

export type JsonLinechartOverview = JsonLinechartPart[];

export type JsonLinechartExchange = JsonLinechartPart[];

/**
 * A subaccount (leaf) as delivered nested inside {@link JsonAccountStats} by the
 * `Accounts` WS command.
 */
export interface JsonAccount {
  id: string;
  name: string;
  ticker: string; // e.g. SQT/USDT
  botTypes: string[];
  group: string; // e.g. 'A1'
  inviteLink?: string;
  projectName: string;
}

/**
 * A broken/errored account exactly as the backend sends it: the `errors[]` array of
 * the `Accounts` response, the parsed `Enable/DisableAccount` result, and the Admin
 * `Servers` command's `monitorStatus.accounts[]`. Structurally a subset of
 * {@link AccountView}.
 */
export interface JsonAccountError {
  name: string;
  label: string;
  status: number;
  message: string;
  proxy: string;
}

/**
 * Flattened account row consumed by the account tables/lists. Built by the frontend
 * (`prepareAccounts`) from {@link JsonAccountStats} + {@link JsonAccount} for healthy
 * accounts, and widened from {@link JsonAccountError} for broken ones. This is a
 * view-model, not a wire type.
 */
export interface AccountView {
  name: string;
  label: string;
  status: number;
  message: string;
  proxy: string;
  inviteLink?: string;
  group?: number;
  exchange?: number;
  botType?: string; // 'MM2'
  ticker?: string; // 'USDT/BTC'
}

export interface JsonAccountStats {
  accounts: JsonAccount[]; // subaccounts (there should be related info in pairs as well)
  // -1 - error, 0 - offline, 1 - warning, 2 - online/success
  // NOTE: `prepareAccounts` maps this through `AccountStatus` (-2..1), which disagrees with
  // the scale above; confirm the backend contract before relying on either (SKY-5069).
  status?: number;
  error?: string;
  usd: number; // current balance in USD
  linechartWeek?: JsonLinechartExchange;
  linechartYear?: JsonLinechartExchange;
  exchange: number;
}

export interface JsonAccountsSuccess extends WsCommand {
  command: 'Accounts';
  id: number;
  accounts: JsonAccountStats[];
  errors: JsonAccountError[];
}

export interface UpdateAccountState {
  id: string;
  status: RequestStatus;
}

export interface JsonAccountEnableSuccess extends JsonWsSuccessResponse {
  result: string;
  optional: string;
}

export interface JsonAccountDisableSuccess extends JsonWsSuccessResponse {
  id: string;
}

export interface AccountsState {
  requestStatus: RequestStatus;
  error: null | GenericError;

  stats: JsonAccountStats[];
  list: AccountView[];
  brokenAccounts: AccountView[];

  resUpdateAccount: UpdateAccountState | null;
}
